import PropTypes from 'prop-types';
import React from 'react';
import { Alert, Grid, Stack } from '@mui/material';
import { FieldArray } from 'formik';
import { useTranslation } from 'react-i18next';
import CustomSelect from '../../CustomSelect';
import HistoryRange from '../../../../HistoryRange';
import CustomLabel from '../../CustomLabel';
import SensorSelector from './SensorSelector';
import ErrorFeedbackP from '../../ErrorFeedbackP';
import CustomFormControl from '../../CustomFormControl';
import { useGetAll } from '../../../../../hooks/useGetAll';

const ChartSelector = ({
  handleBlur,
  handleChange,
  setFieldValue,
  values,
  errors,
  touched,
  submitForm,
  location,
  config,
}) => {
  const {
    data: { asset: assets },
  } = useGetAll('asset', {
    filter: {
      location,
    },
  });
  const { t } = useTranslation();

  return (
    <form onSubmit={submitForm}>
      <Grid container spacing={2} sx={{ width: '500px' }}>
        <Grid item xs={12}>
          <Alert severity="info" variant="filled" sx={{ width: 'fit-content' }}>
            {t('atoms.widget.selector.chart_info')}
          </Alert>
        </Grid>
        <Grid item xs={12}>
          <CustomFormControl
            id="title"
            label={t('atoms.widget.selector.title')}
            value={values.title}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <CustomSelect
            id="chart"
            label={t('atoms.widget.selector.chart')}
            options={(config.options || []).map(c => ({
              value: c,
              label: t(`atoms.widget.selector.options.${c}`),
            }))}
            value={values.chart}
            onChange={handleChange}
            onBlur={handleBlur}
            errors={errors}
            touched={touched}
            required
          />
        </Grid>
        <Grid item xs={6}>
          <Stack justifyContent="flex-start" alignItems="flex-start">
            <CustomLabel htmlFor="time-range" required>
              {t('atoms.widget.selector.time_range')}
            </CustomLabel>
            <HistoryRange
              id="timeRange"
              value={values.timeRange}
              setTimeRange={value =>
                handleChange({ target: { value, id: 'timeRange' } })
              }
              hideCustom
              handleBlur={handleBlur}
              fullWidth
            />
            <ErrorFeedbackP
              touched={touched.timeRange}
              error={errors.timeRange}
            />
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <FieldArray
            name="items"
            render={arrayHelpers =>
              values.items?.map((item, index) => (
                <SensorSelector
                  key={item.id}
                  assets={assets}
                  values={values}
                  arrayHelpers={arrayHelpers}
                  errors={errors}
                  touched={touched}
                  handleBlur={handleBlur}
                  handleChange={handleChange}
                  setFieldValue={setFieldValue}
                  index={index}
                  isList
                  measuringModeFilter={[
                    'positioning',
                    'streaming',
                    'enum',
                    'color',
                  ]}
                  disableActuators
                  disableParts
                />
              ))
            }
          />
        </Grid>
      </Grid>
    </form>
  );
};

ChartSelector.propTypes = {
  errors: PropTypes.any.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  location: PropTypes.string.isRequired,
  submitForm: PropTypes.any.isRequired,
  touched: PropTypes.any.isRequired,
  config: PropTypes.any.isRequired,
  values: PropTypes.shape({
    chart: PropTypes.string,
    title: PropTypes.string,
    items: PropTypes.array,
    timeRange: PropTypes.string,
  }).isRequired,
};

export default ChartSelector;
